import type { FC } from 'react';
import { useEffect } from 'react';
import {
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { WorkOrderExecutionResult } from '@evimo/types/enums/maintenance-execution';
import type { KanbanTicketView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import { useWorkOrderCompleteExecutionResultOptions } from './hooks/useWorkOrderCompleteExecutionResultOptions';

export type WorkOrderCompleteTicketPayload = {
  ticketId: string;
  executionResult: string;
  summary?: string;
  technicianComment?: string;
  workPerformed?: string;
  recommendation?: string;
  confirmedSerialNumber?: string;
  confirmedAssetIdentifier?: string;
  pauseDurationMinutes?: number;
  arrivedAt?: Date;
  faultCategory?: string;
  faultDescription?: string;
};

export interface WorkOrderCompleteModalProps {
  open: boolean;
  workOrderCode: string;
  tickets: KanbanTicketView[];
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (completions: WorkOrderCompleteTicketPayload[]) => void;
}

type TicketFormRow = {
  executionResult: string;
  summary?: string;
  technicianComment?: string;
  workPerformed?: string;
  recommendation?: string;
  confirmedSerialNumber?: string;
  confirmedAssetIdentifier?: string;
  pauseDurationMinutes?: number | null;
  arrivedAt?: Dayjs | null;
  faultCategory?: string;
  faultDescription?: string;
};

type CompleteFormValues = {
  tickets: Record<string, TicketFormRow>;
};

function emptyTicketRow(ticket: KanbanTicketView): TicketFormRow {
  return {
    executionResult: WorkOrderExecutionResult.COMPLETED_OK,
    summary: '',
    technicianComment: '',
    workPerformed: '',
    recommendation: '',
    confirmedSerialNumber: ticket.assetSerialNumber ?? '',
    confirmedAssetIdentifier: ticket.assetName ?? '',
    pauseDurationMinutes: null,
    arrivedAt: null,
    faultCategory: '',
    faultDescription: '',
  };
}

function trimOrUndefined(value: string | undefined): string | undefined {
  const next = value?.trim();
  return next ? next : undefined;
}

export const WorkOrderCompleteModal: FC<WorkOrderCompleteModalProps> = ({
  open,
  workOrderCode,
  tickets,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation('maintenance');
  const [form] = Form.useForm<CompleteFormValues>();
  const executionResultOptions = useWorkOrderCompleteExecutionResultOptions();

  useEffect(() => {
    if (!open) return;
    const initial: CompleteFormValues = { tickets: {} };
    for (const ticket of tickets) {
      initial.tickets[ticket.id] = emptyTicketRow(ticket);
    }
    form.setFieldsValue(initial);
  }, [form, open, tickets]);

  return (
    <Modal
      open={open}
      title={t('workOrders.kanban.complete.title', { code: workOrderCode })}
      okText={t('workOrders.kanban.complete.confirm')}
      cancelText={t('workOrders.kanban.cancel')}
      confirmLoading={loading}
      width={720}
      onCancel={onCancel}
      onOk={() => {
        void form.validateFields().then((values) => {
          onConfirm(
            tickets.map((ticket) => {
              const row = values.tickets[ticket.id];
              return {
                ticketId: ticket.id,
                executionResult: row.executionResult,
                summary: trimOrUndefined(row.summary),
                technicianComment: trimOrUndefined(row.technicianComment),
                workPerformed: trimOrUndefined(row.workPerformed),
                recommendation: trimOrUndefined(row.recommendation),
                confirmedSerialNumber: trimOrUndefined(
                  row.confirmedSerialNumber,
                ),
                confirmedAssetIdentifier: trimOrUndefined(
                  row.confirmedAssetIdentifier,
                ),
                pauseDurationMinutes:
                  row.pauseDurationMinutes == null
                    ? undefined
                    : row.pauseDurationMinutes,
                arrivedAt: row.arrivedAt?.toDate(),
                faultCategory: trimOrUndefined(row.faultCategory),
                faultDescription: trimOrUndefined(row.faultDescription),
              };
            }),
          );
        });
      }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        {t('workOrders.kanban.complete.description')}
      </Typography.Paragraph>
      <Form form={form} layout="vertical">
        <Collapse
          defaultActiveKey={tickets.map((ticket) => ticket.id)}
          items={tickets.map((ticket) => ({
            key: ticket.id,
            label: `${ticket.code} — ${ticket.title}`,
            children: (
              <Space orientation="vertical" style={{ width: '100%' }} size={12}>
                <Form.Item
                  name={['tickets', ticket.id, 'executionResult']}
                  label={t('workOrders.kanban.complete.executionResult')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'workOrders.kanban.complete.executionResultRequired',
                      ),
                    },
                  ]}
                >
                  <Select options={executionResultOptions} />
                </Form.Item>
                <Form.Item
                  name={['tickets', ticket.id, 'summary']}
                  label={t('workOrders.kanban.complete.summary')}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item
                  name={['tickets', ticket.id, 'technicianComment']}
                  label={t('workOrders.kanban.complete.technicianComment')}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>

                <Collapse
                  size="small"
                  items={[
                    {
                      key: 'more',
                      label: t('workOrders.kanban.complete.moreDetails'),
                      children: (
                        <Space
                          orientation="vertical"
                          style={{ width: '100%' }}
                          size={12}
                        >
                          <Form.Item
                            name={['tickets', ticket.id, 'workPerformed']}
                            label={t(
                              'workOrders.kanban.complete.workPerformed',
                            )}
                          >
                            <Input.TextArea rows={2} />
                          </Form.Item>
                          <Form.Item
                            name={['tickets', ticket.id, 'recommendation']}
                            label={t(
                              'workOrders.kanban.complete.recommendation',
                            )}
                          >
                            <Input.TextArea rows={2} />
                          </Form.Item>
                          <Form.Item
                            name={[
                              'tickets',
                              ticket.id,
                              'confirmedSerialNumber',
                            ]}
                            label={t(
                              'workOrders.kanban.complete.confirmedSerialNumber',
                            )}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[
                              'tickets',
                              ticket.id,
                              'confirmedAssetIdentifier',
                            ]}
                            label={t(
                              'workOrders.kanban.complete.confirmedAssetIdentifier',
                            )}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[
                              'tickets',
                              ticket.id,
                              'pauseDurationMinutes',
                            ]}
                            label={t(
                              'workOrders.kanban.complete.pauseDurationMinutes',
                            )}
                          >
                            <InputNumber
                              min={0}
                              style={{ width: '100%' }}
                              placeholder="0"
                            />
                          </Form.Item>
                          <Form.Item
                            name={['tickets', ticket.id, 'arrivedAt']}
                            label={t('workOrders.kanban.complete.arrivedAt')}
                          >
                            <DatePicker
                              showTime
                              style={{ width: '100%' }}
                              disabledDate={(current) =>
                                Boolean(current && current.isAfter(dayjs()))
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            name={['tickets', ticket.id, 'faultCategory']}
                            label={t(
                              'workOrders.kanban.complete.faultCategory',
                            )}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={['tickets', ticket.id, 'faultDescription']}
                            label={t(
                              'workOrders.kanban.complete.faultDescription',
                            )}
                          >
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Space>
            ),
          }))}
        />
      </Form>
    </Modal>
  );
};
